import React, { useMemo, useState } from 'react';
import type { CheckedState } from '@radix-ui/react-checkbox';
import { SlidersHorizontal, CheckCircle2, XCircle } from 'lucide-react';
import type { CopilotTool } from '@/types/copilot';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

interface ToolSelectionControlProps {
  tools: CopilotTool[];
  selectedToolIds: string[];
  onChange: (nextSelection: string[]) => void;
  onSelectAll: () => void;
  onDeselectAll: () => void;
}

export const ToolSelectionControl: React.FC<ToolSelectionControlProps> = ({
  tools,
  selectedToolIds,
  onChange,
  onSelectAll,
  onDeselectAll,
}) => {
  const [open, setOpen] = useState(false);

  const summary = useMemo(() => {
    if (!tools.length) {
      return 'No tools available';
    }

    if (selectedToolIds.length === 0) {
      return 'No tools selected';
    }

    if (selectedToolIds.length === tools.length) {
      return 'All tools active';
    }

    return `${selectedToolIds.length} of ${tools.length} selected`;
  }, [selectedToolIds.length, tools.length]);

  const handleToggle = (toolId: string, checked: CheckedState) => {
    const isChecked = checked === true;
    const alreadySelected = selectedToolIds.includes(toolId);

    if (isChecked && !alreadySelected) {
      onChange([...selectedToolIds, toolId]);
    } else if (!isChecked && alreadySelected) {
      onChange(selectedToolIds.filter(id => id !== toolId));
    }
  };

  if (!tools.length) {
    return null;
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="mt-3 w-full justify-between bg-white"
        >
          <span className="flex items-center gap-2 text-sm font-medium">
            <SlidersHorizontal className="h-4 w-4 text-primary" />
            Manage tools
          </span>
          <span className="text-xs text-muted-foreground">{summary}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-4" align="center">
        <div className="flex items-center justify-between mb-3">
          <div className="text-sm font-medium text-gray-900">Active tools</div>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-8 px-2 text-xs"
              onClick={onSelectAll}
            >
              <CheckCircle2 className="mr-1 h-4 w-4" />
              Select all
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-8 px-2 text-xs"
              onClick={onDeselectAll}
            >
              <XCircle className="mr-1 h-4 w-4" />
              Clear
            </Button>
          </div>
        </div>

        <ScrollArea className="max-h-64 pr-2">
          <div className="space-y-2">
            {tools.map(tool => (
              <label
                key={tool.id}
                className="flex cursor-pointer items-start gap-2 rounded-md border border-transparent px-2 py-2 transition-colors hover:border-primary/40 hover:bg-primary/5"
              >
                <Checkbox
                  checked={selectedToolIds.includes(tool.id)}
                  onCheckedChange={checked => handleToggle(tool.id, checked)}
                  className="mt-1"
                />
                <div className="space-y-1">
                  <div className="text-sm font-medium text-gray-900">{tool.name}</div>
                  {tool.description && (
                    <p className="text-xs text-gray-500 leading-snug">{tool.description}</p>
                  )}
                </div>
              </label>
            ))}
          </div>
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
};
